import React from 'react'
import { format } from 'date-fns'
import {
  getCurrentProfileSelect,
  PROFILE_KEY,
  WEIGHT_UNIT_MESSAGE
} from '@gymaholic/packages'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import useIntl from 'modules/component/utils/hooks/useIntl'
import { View } from 'components/View'
import Text from 'components/Text'
import ButtonPrimary from 'components/ButtonPrimary'
import loadProfileLogsAction from 'modules/profile-log/redux/actions/load'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import { LoadingIcon } from 'components/Icon/icons/loading'
import Button from 'components/Button'
import '../victory-chart-scroll-events.css'
import {
  getWeightGraphDataSelector,
  isLoadedProfileLogsSelector,
  isLoadingProfileLogsSelector
} from 'modules/profile-log/redux/selectors/index.js'
import TextTitleCondensed from 'components/TextTitleCondensed.js'
import { defineMessages } from 'react-intl'

let VictoryAxis, VictoryChart, VictoryLabel, VictoryLine, VictoryScatter

const messages = defineMessages({
  weight: {
    id: 'WeightGraph.weight',
    defaultMessage: 'Weight'
  },
  goal: {
    id: 'WeightGraph.goal',
    defaultMessage: 'Goal'
  },
  addWeight: {
    id: 'WeightGraph.addWeight',
    defaultMessage: 'Add Weight'
  }
})

const dateFormat = 'EEEEE'

export function WeightGraph ({
  handleClickAddWeight,
  handlePressWeight
}) {
  const customerProfile = useSelector(getCurrentProfileSelect)
  const weight = getValueReduxUtil(customerProfile, PROFILE_KEY.WEIGHT)
  const weightUnit = getValueReduxUtil(customerProfile, PROFILE_KEY.WEIGHT_UNIT)
  const weightGoal = getValueReduxUtil(customerProfile, PROFILE_KEY.WEIGHT_GOAL)
  const weightGraphData = useSelector(getWeightGraphDataSelector())
  const isProfileLogsLoaded = useSelector(isLoadedProfileLogsSelector())
  const isProfileLogsLoading = useSelector(isLoadingProfileLogsSelector())
  const [isVictoryLoaded, setIsVictoryLoaded] = React.useState(false)

  const intl = useIntl()
  const dispatch = useDispatch()

  const hasNoData = !weightGraphData || weightGraphData.data.length === 0

  React.useEffect(() => {
    if (!isProfileLogsLoaded) {
      dispatch(loadProfileLogsAction(1))
    }
  }, [])

  React.useEffect(() => {
    const loadVictory = async () => {
      if (!isVictoryLoaded && !hasNoData && !isProfileLogsLoading) {
        const victory = await import(/* webpackChunkName: "victory" */ 'victory')
        VictoryAxis = victory.VictoryAxis
        VictoryChart = victory.VictoryChart
        VictoryLabel = victory.VictoryLabel
        VictoryLine = victory.VictoryLine
        VictoryScatter = victory.VictoryScatter
        setIsVictoryLoaded(true)
      }
    }
    loadVictory()
  }, [isProfileLogsLoading, hasNoData, isVictoryLoaded])

  if (isProfileLogsLoading) {
    return (
      <View
        h='50px'
        mt='16px'
        mx='32px'
        ac
        row
        px='16px'
        style={{
          border: '1px solid #DDDDDD',
          borderRadius: '6px',
          justifyContent: 'space-between'
        }}
      >
        <Text
          fs='1.125em'
          fw='800'
          pb='4px'
          style={{
            transform: 'scaleX(0.8) scaleY(1.2)',
            transformOrigin: '0 0',
            textTransform: 'uppercase'
          }}
        >
          {formatMessageIntlUtil(intl, messages.weight)}
        </Text>
        <LoadingIcon
          stroke='black'
          style={{
            width: '30px',
            height: '30px'
          }}
        />
      </View>
    )
  }

  const YTicks = ({
    x,
    y,
    datum
  }) => {
    return (
      <text
        x={x - 44}
        y={y}
        style={{
          fill: weightGoal ? Number(datum) === weightGoal ? '#43D051' : '#151D21' : '#151D21',
          fontSize: '16px',
          fontWeight: 590
        }}
      >{datum}
      </text>
    )
  }

  const renderGraph = () => {
    if (!isVictoryLoaded) {
      return null
    }

    return (
      <View
        pl='15px'
      >
        {weightGraphData.data.length > 0 && (
          <VictoryChart
            height={300}
            padding={{
              left: 60,
              right: 60,
              bottom: 40,
              top: 40
            }}
          >
            <VictoryAxis
              dependentAxis
              domain={weightGraphData.domain}
              tickValues={weightGraphData.tickValues}
              tickLabelComponent={<YTicks />}
              style={{
                axis: { strokeWidth: 0 }
              }}
            />
            <VictoryAxis
              tickFormat={(date) => format(date, dateFormat)}
              tickValues={weightGraphData.dates}
              style={{
                axis: { strokeWidth: 0 },
                tickLabels: {
                  fill: '#7F8D94',
                  fontSize: '16px',
                  fontFamily: 'unset',
                  fontWeight: '590'
                }
              }}
            />
            <VictoryLine
              padding={0}
              animate={{
                duration: 2000,
                onLoad: { duration: 1000 }
              }}
              interpolation='natural'
              standalone={false}
              singleQuadrantDomainPadding={false}
              data={weightGraphData.data}
              style={{
                data: {
                  stroke: '#151D21',
                  strokeWidth: 3
                }
              }}
            />
            {/* Weight goal */}
            {weightGoal && (
              <VictoryLine
                padding={0}
                labelComponent={
                  <VictoryLabel
                    verticalAnchor='middle'
                    textAnchor='start'
                  />
                }
                labels={(props) => {
                  if (Number(props.index) === 0) {
                    return formatMessageIntlUtil(intl, messages.goal)
                  }
                }}
                animate={{
                  duration: 2000,
                  onLoad: { duration: 1000 }
                }}
                interpolation='natural'
                standalone={false}
                singleQuadrantDomainPadding={false}
                data={weightGraphData.weightGoalLineData}
                style={{
                  data: {
                    stroke: '#43D051',
                    strokeWidth: 3
                  },
                  labels: {
                    fontSize: '16px',
                    fill: '#43D051',
                    fontWeight: 590
                  }
                }}
              />
            )}
            <VictoryScatter
              data={[weightGraphData.data[weightGraphData.data.length - 1]]}
              size={6}
              style={{
                data: {
                  fill: '#151D21',
                  stroke: '#ffffff',
                  strokeWidth: 4
                }
              }}
            />
          </VictoryChart>
        )}
      </View>
    )
  }

  if (hasNoData) {
    return <></>
  }

  if (hasNoData) {
    return (
      <View
        mt='16px'
        mx='32px'
        py='11px'
        style={{
          border: '1px solid #DDDDDD',
          borderRadius: '6px'
        }}
      >
        <View
          px='16px'
          row
          style={{
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <TextTitleCondensed>
            {formatMessageIntlUtil(intl, messages.weight)}
          </TextTitleCondensed>
          <ButtonPrimary
            style={{
              height: '32px',
              minWidth: '8em'
            }}
            onClick={handleClickAddWeight}
          >
            <Text
              fs='0.8em'
              fw='600'
              c='#FFFFFF'
              style={{ textTransform: 'uppercase' }}
            >
              {formatMessageIntlUtil(intl, messages.addWeight)}
            </Text>
          </ButtonPrimary>
        </View>
      </View>
    )
  }

  return (
    <View
      mt='16px'
      mx='32px'
      pb='10px'
      style={{
        border: '1px solid #DDDDDD',
        borderRadius: '6px'
      }}
    >
      <View
        mt='16px'
        px='16px'
        row
        ac
        style={{ justifyContent: 'space-between' }}
      >
        <Text
          fs='1.125em'
          fw='800'
          pb='7px'
          style={{
            transform: 'scaleX(0.8) scaleY(1.2)',
            transformOrigin: '0 0',
            textTransform: 'uppercase'
          }}
        >
          {formatMessageIntlUtil(intl, messages.weight)}
        </Text>
        <Button
          onClick={handlePressWeight}
          style={{ height: 'unset' }}
        >
          <Text
            fs='1.125em'
            fw='800'
            c='#151D21'
            style={{
              transform: 'scaleX(0.8) scaleY(1.2)',
              transformOrigin: '0 0',
              textTransform: 'uppercase'
            }}
          >
            {weight}
            <span style={{
              fontSize: '0.7em',
              color: '#151D21'
            }}
            >{formatMessageIntlUtil(intl, WEIGHT_UNIT_MESSAGE[weightUnit]).toUpperCase()}
            </span>
          </Text>
        </Button>
      </View>
      {renderGraph()}
    </View>
  )
}
